import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import posthog from "@/lib/posthog";
import MerchantContext from "@/contexts/MerchantContext";
import type { Lane } from "./progress";

const SELECTION_CAP = 500;

type ScopeSwitchPrompt = {
  current: Lane;
  resolve: (confirmed: boolean) => void;
};

type InactivoSelectionApi = {
  enabled: boolean;
  scope: Lane | null;
  ids: Set<string>;
  capHit: boolean;
  selectAllUsed: boolean;
  isActive: (laneId: string) => boolean;
  toggle: (leadId: string, lane: Lane) => void;
  selectAllInLane: (lane: Lane, ids: string[], capped: boolean) => void;
  clear: () => void;
  pendingScopeSwitch: ScopeSwitchPrompt | null;
};

const InactivoSelectionContext = createContext<InactivoSelectionApi | null>(null);

type ProviderProps = {
  resetKey: string;
  enabled: boolean;
  children: ReactNode;
};

export const InactivoSelectionProvider = ({ resetKey, enabled, children }: ProviderProps) => {
  const { merchantId } = useContext(MerchantContext);
  const [scope, setScope] = useState<Lane | null>(null);
  const [ids, setIds] = useState<Set<string>>(() => new Set());
  const [capHit, setCapHit] = useState(false);
  const [selectAllUsed, setSelectAllUsed] = useState(false);
  const [pendingScopeSwitch, setPendingScopeSwitch] =
    useState<ScopeSwitchPrompt | null>(null);

  const trackSelectionStarted = useCallback(
    (lane: Lane) =>
      posthog.capture("tablero selection started", {
        merchant_id: merchantId,
        lane_id: lane.id,
      }),
    [merchantId]
  );

  const resetAll = useCallback(() => {
    setIds(new Set());
    setScope(null);
    setCapHit(false);
    setSelectAllUsed(false);
  }, []);

  // Auto-clear whenever the parent's reset key changes (filter change), or if
  // broadcast becomes disabled mid-session.
  useEffect(() => {
    resetAll();
  }, [resetKey, resetAll]);

  useEffect(() => {
    if (!enabled) resetAll();
  }, [enabled, resetAll]);

  const isActive = useCallback((laneId: string) => scope?.id === laneId, [scope]);

  const toggleInScope = useCallback((leadId: string, lane: Lane) => {
    setScope((cur) => cur ?? lane);
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        if (next.size >= SELECTION_CAP) {
          toast.error(`Máximo ${SELECTION_CAP} leads por envío`);
          return prev;
        }
        next.add(leadId);
      }
      return next;
    });
  }, []);

  const toggle = useCallback(
    (leadId: string, lane: Lane) => {
      if (!enabled) return;
      // Different lane and current selection non-empty → ask before switching.
      if (scope && scope.id !== lane.id) {
        if (ids.size === 0) {
          setScope(lane);
          toggleInScope(leadId, lane);
          return;
        }
        setPendingScopeSwitch({
          current: scope,
          resolve: (confirmed) => {
            setPendingScopeSwitch(null);
            if (!confirmed) return;
            setIds(new Set());
            setCapHit(false);
            setSelectAllUsed(false);
            setScope(lane);
            trackSelectionStarted(lane);
            // setIds runs in the next microtask but toggleInScope below queues
            // a state update from the empty set, so the lead is added cleanly.
            toggleInScope(leadId, lane);
          },
        });
        return;
      }
      if (!scope) trackSelectionStarted(lane);
      toggleInScope(leadId, lane);
    },
    [enabled, scope, ids.size, trackSelectionStarted, toggleInScope]
  );

  const selectAllInLane = useCallback(
    (lane: Lane, nextIds: string[], capped: boolean) => {
      if (!enabled) return;
      if (!scope) trackSelectionStarted(lane);
      setScope(lane);
      setIds(new Set(nextIds));
      setCapHit(capped);
      setSelectAllUsed(true);
      if (capped) {
        toast.message(`Se alcanzó el tope de ${SELECTION_CAP} leads`);
      }
    },
    [enabled, scope, trackSelectionStarted]
  );

  const clear = useCallback(() => {
    posthog.capture("tablero selection cleared", {
      merchant_id: merchantId,
      lane_id: scope?.id,
      lead_count: ids.size,
    });
    resetAll();
  }, [merchantId, scope, ids.size, resetAll]);

  const api = useMemo<InactivoSelectionApi>(
    () => ({
      enabled,
      scope,
      ids,
      capHit,
      selectAllUsed,
      isActive,
      toggle,
      selectAllInLane,
      clear,
      pendingScopeSwitch,
    }),
    [
      enabled,
      scope,
      ids,
      capHit,
      selectAllUsed,
      isActive,
      toggle,
      selectAllInLane,
      clear,
      pendingScopeSwitch,
    ]
  );

  return (
    <InactivoSelectionContext.Provider value={api}>
      {children}
    </InactivoSelectionContext.Provider>
  );
};

export const useInactivoSelection = (): InactivoSelectionApi => {
  const ctx = useContext(InactivoSelectionContext);
  if (!ctx)
    throw new Error("useInactivoSelection must be used inside <InactivoSelectionProvider />");
  return ctx;
};
